import ModalPromotion from '@/components/sections/builder/elements/body/cart-total/component/modal-promotion';
import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { Button } from '@/components/ui/button';
import { useGetListPromotions } from '@/hooks/api/storefront/promotion';
import useModal from '@/hooks/useModal';
import { CheckCircle2, Ticket } from 'lucide-react';
import Cookies from 'js-cookie';
import {
  calculateTotalDiscountPerPriority,
  cn,
  formatIDR,
  generatePromotionsPayload,
} from '@/lib/utils';
import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { Skeleton } from '@/components/ui/skeleton';
import CardVoucher from '@/components/sections/builder/elements/body/cart-total/component/card-voucher';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePromotionStore } from '@/stores/promotion';
import { shallow } from 'zustand/shallow';
import { ThemeType, useBuilderStore } from '@/stores/builder';
import FixedCheckoutButton from '@/components/sections/builder/elements/body/cart-total/component/fixed-checkout-button';
import ModalAlert from '@/components/sections/modal-alert';
import { toast } from '@/components/ui/use-toast';
import { BaseCart } from '@/types/http/cart/common';
import { Card, CardContent } from '@/components/ui/card';
import { dummyPromotions } from '@/constant/dummyData/dummy-carts';
import { ElementDataType } from '@/components/sections/builder/elements/body/cart-total/type';
import { ListProductFreeGift } from '@/components/sections/builder/elements/body/cart-total/component/list-product-free-gift';
import { buildAutoFreeGifts, detectConflictPromotion, ensureApplied } from '@/lib/promotions';
import {
  useAutoApplyBestSpendingPromo,
  useAutoApplySpecificFreeGift,
  usePruneSpendingPromos,
} from '@/hooks/use-promotion';

export default function ElementContent({
  element,
  data,
  preview,
  theme,
  isLoadingData,
}: ElementProps) {
  const childs = element.childs || [];
  const modalPromotion = useModal();
  const modalOverlap = useModal();
  const token = Cookies.get('accessToken') || '';

  const content = element.data as ElementDataType;

  const [pendingVoucher, setPendingVoucher] = useState<{
    promoRewards: BasePromotion[];
    code: string;
  } | null>(null);

  const {
    selectedPromotion,
    addPromotion,
    removePromotion,
    selectedFreeGift,
    removeFreeGift,
    replaceFreeGift,
    syncFreeGiftPromotion,
    applyVoucher,
    applyVoucherRewards,
  } = usePromotionStore(
    (s) => ({
      selectedPromotion: s.selectedPromotion,
      addPromotion: s.addPromotion,
      removePromotion: s.removePromotion,
      applyVoucherRewards: s.applyVoucherRewards,
      applyVoucher: s.applyVoucher,
      syncFreeGiftPromotion: s.syncFreeGiftPromotion,
      selectedFreeGift: s.selectedFreeGift,
      removeFreeGift: s.removeFreeGift,
      replaceFreeGift: s.replaceFreeGift,
      removeFreeGiftByItemId: s.removeFreeGiftByItemId,
    }),
    shallow
  );
  const themeBuilder = useBuilderStore((state) => state.theme, shallow);
  const themeLayout = preview ? themeBuilder : theme;
  const promotionTheme = themeLayout?.promotion;

  const payloadListPromotion = useMemo(() => {
    const items = (data?.items as BaseCart[]) || [];

    const { mappedItems, grandTotal } = items.reduce(
      (acc, item) => {
        const discountPrice = item.discount_price ?? 0;
        const discountAmount = item.discount_amount ?? 0;
        const sellPrice = item.sell_price;

        const pricePerItem = discountPrice > 0 ? discountPrice : sellPrice;
        const itemTotal = item.quantity * pricePerItem;

        acc.mappedItems.push({
          item_id: item.id,
          qty: item.quantity,
          amount: itemTotal,
        });

        acc.grandTotal += itemTotal - discountAmount;

        return acc;
      },
      {
        mappedItems: [] as {
          item_id: string;
          qty: number;
          amount: number;
        }[],
        grandTotal: 0,
      }
    );

    return {
      items: mappedItems,
      grand_total: grandTotal,
      current_date: new Date().toISOString(),
    };
  }, [data?.items]);

  const { data: recommendPromotion, isLoading: isLoadingRecommend } = useGetListPromotions(
    payloadListPromotion,
    true,
    token,
    {
      enabled: !!payloadListPromotion.items?.length,
    }
  );

  const { data: listPromotion, isLoading: isLoadingPromotion } = useGetListPromotions(
    payloadListPromotion,
    false,
    token,
    {
      enabled: !!payloadListPromotion.items?.length,
    }
  );

  const allPromotions = useMemo(() => {
    const selectedIds = new Set(selectedPromotion.map((p) => p.promotion_id));
    const merged = [...selectedPromotion];
    listPromotion?.forEach((promo) => {
      if (!selectedIds.has(promo.promotion_id)) {
        merged.push(promo);
      }
    });
    return merged;
  }, [selectedPromotion, listPromotion]);

  const listPromotionDiscount = useMemo(() => {
    return selectedPromotion
      ?.filter((promo) => Array.isArray(promo.discount_reward) && promo.discount_reward.length > 0)
      .sort((a, b) => Number(a.priority) - Number(b.priority));
  }, [selectedPromotion]);

  const listPromotionFreeGift = useMemo(() => {
    return selectedPromotion.some(
      (promo) => promo.product_reward && promo.product_reward.length > 0
    );
  }, [selectedPromotion]);

  const { updatedPromos, totalDiscount } = calculateTotalDiscountPerPriority(
    listPromotionDiscount,
    preview ? 500000 : payloadListPromotion.grand_total
  );

  const handleSelectPromo = (promo: BasePromotion) => {
    addPromotion(promo, payloadListPromotion.grand_total);
  };

  const handleUnselectPromo = (promoId: string) => {
    removePromotion(promoId);
  };

  const handleApplyVoucher = useCallback(
    (voucherIds: string[]) => {
      if (!allPromotions) return;

      const selected = allPromotions.filter((p) => voucherIds.includes(p.promotion_id));
      const hasNonOverlap = selected.some((p) => p.is_overlap === false);
      const finalPromos = hasNonOverlap ? selected : selected.filter((p) => p.is_overlap === true);

      const prevIds = new Set(selectedPromotion.map((p) => p.promotion_id));
      const nextIds = new Set(finalPromos.map((p) => p.promotion_id));

      applyVoucher(voucherIds, allPromotions);

      prevIds.forEach((id) => {
        if (!nextIds.has(id)) {
          removeFreeGift(id);
        }
      });

      for (const promo of finalPromos) {
        const hasApplied = (promo.applied_product_reward?.length ?? 0) > 0;
        const hasInclude = (promo.product_reward ?? []).some(
          (r) => r.reward_type === 'product' && r.rule_type === 'include'
        );
        if (!hasApplied || !hasInclude) continue;

        const gifts = buildAutoFreeGifts(promo);
        if (gifts.length > 0) {
          replaceFreeGift(promo.promotion_id, gifts, 'replace');
        }
      }

      syncFreeGiftPromotion();
    },
    [
      allPromotions,
      selectedPromotion,
      applyVoucher,
      removeFreeGift,
      replaceFreeGift,
      syncFreeGiftPromotion,
    ]
  );

  const applyGiftsForActivePromos = (
    activePromos: BasePromotion[],
    sourcePromos: BasePromotion[]
  ) => {
    for (const promo of activePromos) {
      const enriched = ensureApplied(promo, sourcePromos);

      const hasInclude = (enriched.product_reward ?? []).some(
        (r) => r.reward_type === 'product' && r.rule_type === 'include'
      );

      if (!hasInclude) {
        removeFreeGift(enriched.promotion_id);
        continue;
      }

      const gifts = buildAutoFreeGifts(enriched);

      if (gifts.length > 0) {
        replaceFreeGift(enriched.promotion_id, gifts as any, 'replace');
      } else {
        removeFreeGift(enriched.promotion_id);
      }
    }
    syncFreeGiftPromotion();
  };

  const handleSubmitRedeemVoucher = (promoRewards: BasePromotion[], code: string) => {
    const conflictPromo = detectConflictPromotion(selectedPromotion || [], promoRewards);

    if (conflictPromo) {
      setPendingVoucher({ promoRewards, code });
      modalOverlap.setIsShow(true);
      return;
    }

    const prevIds = new Set(promoRewards.map((p) => p.promotion_id));

    const isNewOverlapFalse = promoRewards.some((promo) => promo.is_overlap === false);
    applyVoucherRewards(promoRewards, code, isNewOverlapFalse);

    const nextSelected = promoRewards;
    const nextIds = new Set(nextSelected.map((p) => p.promotion_id));

    prevIds.forEach((id) => {
      if (!nextIds.has(id)) {
        removeFreeGift(id);
      }
    });

    const sources = [...promoRewards, ...(listPromotion ?? [])];
    applyGiftsForActivePromos(nextSelected, sources);

    toast({
      title: 'Success',
      variant: 'success',
      description: 'Promotion has been applied',
    });
  };

  const handleConfirmOverlap = useCallback(() => {
    if (!pendingVoucher) return;
    const { promoRewards, code } = pendingVoucher;

    const prevIds = new Set(selectedPromotion.map((p) => p.promotion_id));

    const isNewOverlapFalse = promoRewards.some((p) => p.is_overlap === false);
    applyVoucherRewards(promoRewards, code, isNewOverlapFalse);

    const nextSelected = promoRewards;
    const nextIds = new Set(nextSelected.map((p) => p.promotion_id));

    prevIds.forEach((id) => {
      if (!nextIds.has(id)) {
        removeFreeGift(id);
      }
    });

    const sources = [...promoRewards, ...(listPromotion ?? [])];
    applyGiftsForActivePromos(nextSelected, sources);

    modalOverlap.setIsShow(false);
    setPendingVoucher(null);

    toast({
      title: 'Success',
      variant: 'success',
      description: 'Promotion has been applied',
      duration: 3000,
    });
  }, [pendingVoucher, modalOverlap, setPendingVoucher, listPromotion]);

  usePruneSpendingPromos({
    listPromotion,
    grandTotal: payloadListPromotion.grand_total,
    preview,
    isLoadingData: isLoadingData || isLoadingPromotion || isLoadingRecommend,
  });

  useAutoApplySpecificFreeGift({
    listPromotion,
    cartItems: payloadListPromotion.items,
    grandTotal: payloadListPromotion.grand_total,
    preview,
    isLoadingData: isLoadingData || isLoadingPromotion || isLoadingRecommend,
  });

  useAutoApplyBestSpendingPromo({
    listPromotion,
    recommendPromotion,
    grandTotal: payloadListPromotion.grand_total,
    preview,
    isLoadingData: isLoadingData || isLoadingPromotion || isLoadingRecommend,
    skipIfHasNonOverlapSelected: true,
  });

  useEffect(() => {
    syncFreeGiftPromotion();
  }, [selectedPromotion]);

  return (
    <>
      <section className='carttotal-padding theme_cart_page_bg'>
        <div
          className={cn(
            ' mx-auto my-6 px-6 duration-100 md:my-0',
            (themeLayout?.layout?.pageWidth ?? 0) < 800
              ? 'theme_layout_page_width'
              : 'max-w-[1000px]'
          )}
          style={
            {
              '--background': promotionTheme?.colorScheme?.background,
              '--btn-outline': promotionTheme?.colorScheme?.btnOutline,
            } as React.CSSProperties
          }
        >
          {(data?.items?.length > 0 || preview) && (
            <div className='relative flex w-full flex-col gap-2 overflow-hidden '>
              <div className='relative mb-10 flex w-full flex-col items-start gap-4'>
                {/* List promotion discount usage */}
                {(listPromotionDiscount.length > 0 || preview) && (
                  <div className='flex w-full flex-col'>
                    <div className='mb-2 flex items-center justify-between gap-2'>
                      <div
                        className='flex items-center gap-2'
                        style={{ color: promotionTheme?.colorScheme?.text }}
                      >
                        <CheckCircle2 />
                        <p className='text-sm md:text-base'>
                          YOU SAVED {formatIDR(totalDiscount)} ON THIS ORDER
                        </p>
                      </div>
                      <Button
                        variant='link'
                        className='items-center p-0 font-normal'
                        style={{
                          color: promotionTheme?.colorScheme?.textSecondary,
                        }}
                        onClick={() => modalPromotion.open()}
                      >
                        <span className='text-sm'>See all offers</span>
                      </Button>
                    </div>

                    <div className='grid w-full grid-cols-1 gap-2'>
                      {(preview ? dummyPromotions : updatedPromos).map((promo, index) => {
                        return (
                          <CardVoucher
                            key={index}
                            theme={promotionTheme}
                            themeCart={themeLayout?.cart?.pageCartColorScheme}
                            promo={
                              {
                                ...promo,
                                total_discount: promo.total_discount ?? 0,
                              } as BasePromotion & { total_discount: number }
                            }
                            minSpending={promo.minimum_spending}
                            grandTotal={payloadListPromotion.grand_total}
                            onSelect={handleSelectPromo}
                            onRemove={handleUnselectPromo}
                            isRemove={!preview}
                            isShowReward
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* List promotion available */}
                <div className='mb-2 flex w-full items-center justify-between gap-2'>
                  <div
                    className='flex items-center gap-2'
                    style={{ color: promotionTheme?.colorScheme?.text }}
                  >
                    <Ticket />
                    <p className='text-sm md:text-base'>ADD MORE TO SAVE MORE</p>
                  </div>
                  {!preview && !listPromotionDiscount.length && (
                    <Button
                      variant='link'
                      className='items-center p-0 font-normal'
                      style={{
                        color: promotionTheme?.colorScheme?.textSecondary,
                      }}
                      onClick={() => modalPromotion.open()}
                    >
                      <span className='text-sm'>See all offers</span>
                    </Button>
                  )}
                </div>
                <div className='relative flex w-full flex-col gap-4 lg:gap-10'>
                  {isLoadingRecommend ? (
                    <div className='flex flex-col gap-1.5'>
                      <Skeleton className='h-20 w-full' />
                    </div>
                  ) : (preview ? dummyPromotions : recommendPromotion) &&
                    (preview ? dummyPromotions : recommendPromotion)!.length > 0 ? (
                    (preview ? dummyPromotions : recommendPromotion)!.map((promo, index) => {
                      const grandTotal = preview ? 500000 : payloadListPromotion.grand_total;
                      const minSpending = promo.minimum_spending;
                      const percentage = Math.min((grandTotal / minSpending) * 100, 100);

                      return (
                        <CardVoucher
                          key={index}
                          theme={promotionTheme}
                          themeCart={themeLayout?.cart?.pageCartColorScheme}
                          promo={promo as BasePromotion}
                          percentage={percentage}
                          minSpending={minSpending}
                          grandTotal={grandTotal}
                          isEligible
                        />
                      );
                    })
                  ) : (
                    <Card className='border-2 border-dashed border-gray-200 bg-gray-50/50 p-2 text-sm'>
                      <CardContent className='flex items-center gap-3 p-3'>
                        <p className='text-gray-600'>
                          No recommended promotions. In the meantime, check out all available
                          offers.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {(listPromotionFreeGift || preview) && (
                  <ListProductFreeGift
                    colorScheme={promotionTheme?.colorScheme}
                    preview={preview}
                    token={token}
                    isLoading={isLoadingRecommend}
                  />
                )}
              </div>
            </div>
          )}
          {!themeLayout?.cart?.fixedCheckoutButton && (
            <div className='grid-cols-1 md:grid'>
              {/* <div className='flex flex-col items-end text-right'> */}
              <div
                className={cn(
                  'flex flex-col text-right',
                  preview ? 'w-full md:items-end' : 'items-end'
                )}
              >
                {childs.map((child) => (
                  <WrapperChild
                    child={{
                      ...child,
                      parentData: {
                        ...data,
                        promotions_payload: {
                          cart_id: data?.cartId,
                          promotions: generatePromotionsPayload(
                            selectedPromotion,
                            selectedFreeGift.filter((gift) => gift.promotion_id !== undefined) as {
                              promotion_id: string;
                              product_group: BasePromotionRewardProduct;
                              items: (BasePromotionRewardProductItem & {
                                item_id: string;
                                qty?: number;
                              })[];
                            }[]
                          ),
                        },
                      },
                      theme: themeLayout,
                    }}
                    key={child.id}
                    parentType={element.type}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {themeLayout?.cart?.fixedCheckoutButton && (
        <FixedCheckoutButton theme={themeLayout as ThemeType} cart={data} preview={preview} />
      )}

      <ModalPromotion
        openModal={modalPromotion.isShow}
        setOpenModalAction={modalPromotion.setIsShow}
        grandTotal={preview ? 1000000 : payloadListPromotion.grand_total}
        listPromotion={preview ? dummyPromotions : allPromotions}
        onApplyVoucherAction={handleApplyVoucher}
        onRedeemVoucherAction={handleSubmitRedeemVoucher}
        token={token}
        data={data}
        theme={promotionTheme}
        preview={preview}
      />

      <ModalAlert
        openModal={modalOverlap.isShow}
        setOpenModal={modalOverlap.setIsShow}
        title='Are you sure'
        description='These vouchers cannot overlap with existing promotions, do you want to replace them?'
        onConfirm={handleConfirmOverlap}
      />
      <style jsx>
        {`
          @media (min-width: 768px) {
            .carttotal-padding {
              padding-top: ${content.padding?.top || 0}px;
              padding-bottom: ${content.padding?.bottom || 0}px;
              padding-left: ${content.padding?.left || 20}px;
              padding-right: ${content.padding?.right || 20}px;
            }
          }
          @media (max-width: 768px) {
            .carttotal-padding {
              padding-bottom: 20px;
            }
          }
        `}
      </style>
    </>
  );
}
